import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Upload, Phone, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { PWA } from "@/constants/testIds";
import { BRAND } from "./data";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Custom Furniture",
  "Cabinets",
  "Tables",
  "Shelving",
  "Repairs / Restoration",
  "Other Custom Project",
];

const BUDGETS = [
  "Under $2,500",
  "$2,500 – $5,000",
  "$5,000 – $10,000",
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000+",
];

const TIMELINES = [
  "ASAP / Within 1 month",
  "1 – 3 months",
  "3 – 6 months",
  "Flexible / Just exploring",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  project_type: "",
  budget_range: "",
  timeline: "",
  project_details: "",
  inspiration_image_url: "",
};

const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => set("inspiration_image_url", String(reader.result || ""));
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "phone", "email", "project_type", "budget_range", "timeline", "project_details"];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error("Please fill in all required fields.");
        return;
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Quote request received. We'll be in touch within 24 hours.");
      setForm(initial);
      setFileName("");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us or try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid={PWA.quoteFormSection}
      className="relative bg-[#121212] text-[#FAF9F6] py-24 md:py-32 wood-grain-dark"
    >
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-[11px] tracking-[0.4em] uppercase text-[#C19A6B] font-semibold mb-5">
                — Request A Quote
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
                Tell us about <br />
                <span className="italic text-[#D9B27F]">your project.</span>
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-7 text-[#FAF9F6]/70 text-base md:text-lg leading-relaxed font-light">
                Share a few details and we'll be in touch within 24 hours with next steps, ideas,
                and a transparent quote.
              </p>
            </Reveal>

            <Reveal delay={320}>
              <div className="mt-10 space-y-5 border-t border-white/10 pt-8">
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  data-testid="pwa-quote-side-phone"
                  className="flex items-start gap-4 group"
                >
                  <span className="w-10 h-10 border border-[#C19A6B]/40 flex items-center justify-center text-[#C19A6B] group-hover:bg-[#C19A6B] group-hover:text-[#121212] transition-colors">
                    <Phone className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55">Call</p>
                    <p className="text-[#FAF9F6] text-lg group-hover:text-[#D9B27F] transition-colors">
                      {BRAND.phone}
                    </p>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 border border-[#C19A6B]/40 flex items-center justify-center text-[#C19A6B]">
                    <MapPin className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55">Workshop</p>
                    <p className="text-[#FAF9F6] text-lg">South Florida</p>
                  </div>
                </div>
                <a href={`mailto:${BRAND.email}`} className="flex items-start gap-4 group">
                  <span className="w-10 h-10 border border-[#C19A6B]/40 flex items-center justify-center text-[#C19A6B] group-hover:bg-[#C19A6B] group-hover:text-[#121212] transition-colors">
                    <Mail className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55">Email</p>
                    <p className="text-[#FAF9F6] group-hover:text-[#D9B27F] transition-colors">
                      {BRAND.email}
                    </p>
                  </div>
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={200}>
              <div className="bg-[#1A1A1A] border border-[#C19A6B]/15 p-8 md:p-12">
                {done ? (
                  <div
                    data-testid={PWA.quoteFormSuccess}
                    className="py-12 text-center flex flex-col items-center"
                  >
                    <CheckCircle2 className="w-14 h-14 text-[#D9B27F]" />
                    <h3 className="mt-6 font-serif-display text-3xl md:text-4xl">
                      Request received.
                    </h3>
                    <p className="mt-4 text-[#FAF9F6]/70 max-w-md font-light">
                      Thanks for reaching out. We'll review your project and follow up within
                      24 hours with next steps.
                    </p>
                    <button
                      onClick={() => setDone(false)}
                      className="mt-8 btn-outline-gold"
                      data-testid="pwa-quote-form-again"
                    >
                      Submit another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Full Name *
                      </label>
                      <input
                        data-testid={PWA.quoteFormName}
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder="Your name"
                        className="pwa-input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Phone *
                      </label>
                      <input
                        data-testid={PWA.quoteFormPhone}
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="(555) 555-5555"
                        className="pwa-input-dark"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Email *
                      </label>
                      <input
                        type="email"
                        data-testid={PWA.quoteFormEmail}
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="you@email.com"
                        className="pwa-input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Project Type *
                      </label>
                      <select
                        data-testid={PWA.quoteFormProjectType}
                        value={form.project_type}
                        onChange={(e) => set("project_type", e.target.value)}
                        className="pwa-input-dark"
                      >
                        <option value="">Select project type</option>
                        {PROJECT_TYPES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Budget Range *
                      </label>
                      <select
                        data-testid={PWA.quoteFormBudget}
                        value={form.budget_range}
                        onChange={(e) => set("budget_range", e.target.value)}
                        className="pwa-input-dark"
                      >
                        <option value="">Select budget</option>
                        {BUDGETS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Timeline *
                      </label>
                      <select
                        data-testid={PWA.quoteFormTimeline}
                        value={form.timeline}
                        onChange={(e) => set("timeline", e.target.value)}
                        className="pwa-input-dark"
                      >
                        <option value="">Select timeline</option>
                        {TIMELINES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Project Details *
                      </label>
                      <textarea
                        data-testid={PWA.quoteFormDetails}
                        value={form.project_details}
                        onChange={(e) => set("project_details", e.target.value)}
                        rows={4}
                        placeholder="Tell us about your space, vision, materials, and anything else that matters."
                        className="pwa-input-dark resize-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] tracking-[0.32em] uppercase text-[#FAF9F6]/55 font-semibold">
                        Inspiration Image (optional)
                      </label>
                      <label
                        htmlFor={PWA.quoteFormUpload}
                        className="mt-3 flex items-center gap-3 border border-dashed border-white/20 px-4 py-4 cursor-pointer hover:border-[#C19A6B] transition-colors"
                      >
                        <Upload className="w-4 h-4 text-[#C19A6B]" />
                        <span className="text-sm text-[#FAF9F6]/75">
                          {fileName ? fileName : "Upload an inspiration photo"}
                        </span>
                        <input
                          id={PWA.quoteFormUpload}
                          data-testid={PWA.quoteFormUpload}
                          onChange={handleFile}
                          type="file"
                          accept="image/*"
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div className="md:col-span-2 mt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid={PWA.quoteFormSubmit}
                        className="btn-gold w-full justify-center disabled:opacity-60"
                      >
                        {loading ? "Sending..." : "Request My Custom Quote"}
                        {!loading && <ArrowUpRight className="w-4 h-4" />}
                      </button>
                      <p className="mt-3 text-[11px] text-[#FAF9F6]/50 text-center">
                        We respect your time. No spam. No pressure. Just real conversation.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
